import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../firebase';
import { addDoc, collection, getDocs, doc, deleteDoc, setDoc } from 'firebase/firestore';
import BloodFlowLogo from "../../Assets/BloodflowLogo.png";
import BloodFlowLogoNL from "../../Assets/BloodflowLogoNoLetters.png";
import { ref, uploadBytes, listAll, getDownloadURL, getMetadata, deleteObject, updateMetadata } from 'firebase/storage';
import Spinner from '../Spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import DeleteAthletePopup from './DeleteAthletePopup';
import NewFilePopup from './NewFilePopup';
import DeleteTestPopup from './DeleteTestPopup';
import EditAthletePopup from './EditAthletePopup';
import { Activity, ChartBar, FileText, Users, Plus, LogOut, 
  Menu, X, Settings, Upload, Brain, Search, Camera, Heart } from 'lucide-react';
import TestResultsViewer from './TestResultsViewer';
import { AthleteAnalysisDisplay } from './AthleteAnalysisParser';
import '../styles/animations.css';

const AthletesDashboard = () => {
    const [loadingWindow, setLoadingWindow] = useState(true);
    const [nav, setNav] = useState(false);
    const [athletes, setAthletes] = useState([]);
    const [filteredAthletes, setFilteredAthletes] = useState([]);
    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [selectedAthleteTest, setSelectedAthleteTest] = useState([]);
    const [athleteTests, setAthleteTests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showNewFileModal, setShowNewFileModal] = useState(false);
    const [showDeleteTestModal, setShowDeleteTestModal] = useState(false);
    const [showEditAthleteModal, setShowEditAthleteModal] = useState(false);
    const [showAddAthleteForm, setShowAddAthleteForm] = useState(false);
    const [newAthleteData, setNewAthleteData] = useState({
        name: '',
        age: '',
        sex: 'male',
        height: '',
        weight: '',
        sport: '',
        specialty: '',
        notes: ''
    });
    const [trainingPhase, setTrainingPhase] = useState('off-season');
    const [trainingNotes, setTrainingNotes] = useState('');
    const [daysAfterCompetition, setDaysAfterCompetition] = useState('');
    const [trainingIntensity, setTrainingIntensity] = useState('moderate');
    const [recentInjuries, setRecentInjuries] = useState('');
    const [nutritionInfo, setNutritionInfo] = useState('');
    const [sleepQuality, setSleepQuality] = useState('good');
    const [mentalState, setMentalState] = useState('stable');
    const [recoveryState, setRecoveryState] = useState('normal');
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisResults, setAnalysisResults] = useState(null);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
    const [displayedTrainingContext, setDisplayedTrainingContext] = useState({
        trainingPhase: 'off-season',
        trainingIntensity: 'moderate',
        recoveryState: 'normal',
        sleepQuality: 'good',
        mentalState: 'stable',
        daysAfterCompetition: '',
        recentInjuries: '',
        nutritionInfo: ''
    });
    const [profileImage, setProfileImage] = useState(null);
    const analysisContainerRef = useRef(null);

    const fetchAthletes = async () => {
        try {
            const athletesCollection = collection(db, 'fpnUsers');
            const snapshot = await getDocs(athletesCollection);
            const athletesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAthletes(athletesList);
            setFilteredAthletes(athletesList);
        } catch (error) {
            console.error("Error fetching athletes:", error);
        } finally {
            setLoadingWindow(false);
        }
    };

    const fetchAthleteTests = async (athleteId) => {
        try {
            const athleteTestsRef = ref(storage, `fpnResults/${athleteId}`);
            const testFiles = await listAll(athleteTestsRef);

            const fetchedTests = await Promise.all(
                testFiles.items.map(async (itemRef) => {
                    const fileURL = await getDownloadURL(itemRef);
                    const metadata = await getMetadata(itemRef);   

                    let createdAtDate;
                    if (metadata && metadata.customMetadata && metadata.customMetadata.analysisDate) {
                        createdAtDate = new Date(metadata.customMetadata.analysisDate);
                    } else {
                        createdAtDate = new Date(metadata.timeCreated);
                    }
                    
                    const formattedDate = createdAtDate.toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    });
                    
                    return {
                        name: itemRef.name,
                        url: fileURL,
                        createdAt: createdAtDate,
                        date: formattedDate
                    };
                })
            );

            const sortedFiles = fetchedTests.sort((a, b) => a.createdAt - b.createdAt);

            setAthleteTests(sortedFiles.map(file => ({
                name: file.name,
                date: file.date,
                url: file.url,
            })));
        } catch (error) {
            console.error("Error fetching athlete tests:", error);
        }
    };

    useEffect(() => {
        fetchAthletes();
    }, []);

    useEffect(() => {
        if (selectedAthlete) {
            fetchAthleteTests(selectedAthlete.id);
            setAnalysisResults(null);
            fetchProfileImage(selectedAthlete.id);
        }
    }, [selectedAthlete]);

    const fetchProfileImage = async (athleteId) => {
        try {
            const imageRef = ref(storage, `fpnProfiles/${athleteId}`);
            try {
                const url = await getDownloadURL(imageRef);
                setProfileImage(url);
            } catch (error) {
                // If no image exists, reset to default
                setProfileImage(null);
            }
        } catch (error) {
            console.log('Error fetching profile image:', error);
            setProfileImage(null);
        }
    };

    const handleAddAthleteClick = () => {
        setShowAddAthleteForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAthleteData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addAthlete = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
      
        if (!user) {
          console.error("User is not authenticated.");
          return;
        }

        if (!newAthleteData.name || !newAthleteData.age || !newAthleteData.height || !newAthleteData.weight) {
            toast.error("Please fill in all required fields");
            return;
        }
      
        try {
            const newAthlete = {
                ...newAthleteData,
                createdAt: new Date(),
                createdBy: user.uid,
            };
      
            await addDoc(collection(db, 'fpnUsers'), newAthlete);
            toast.success("Athlete added successfully.");
            setShowAddAthleteForm(false);
            setNewAthleteData({
                name: '',
                age: '',
                sex: 'male',
                height: '',
                weight: '',
                sport: '',
                specialty: '',
                notes: ''
            });
            fetchAthletes();
        } catch (error) {
            console.error('Error adding athlete:', error.message);
            toast.error("Failed to add athlete.");
        }
    };

    const handleNewFileClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.multiple = true;
        input.onchange = handleFileChange;
        input.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedAthleteTest(files);
        setShowNewFileModal(true);
    };

    const uploadBloodTest = async (athleteId) => {
        try {
            if (selectedAthleteTest.length === 0) {
                toast.error("No files selected.");
                return;
            }
    
            for (const file of selectedAthleteTest) {
                const fileRef = ref(storage, `fpnResults/${athleteId}/${file.name}`);
                await uploadBytes(fileRef, file);
            }
    
            setShowNewFileModal(false);
            toast.success("Blood test files uploaded successfully.");
            setSelectedAthleteTest([]);
            fetchAthleteTests(athleteId);
        } catch (error) {
            console.error("Error uploading files:", error);
            toast.error("Failed to upload files.");
        }
    };

    const handleAthleteSelect = (athlete) => {
        setSelectedAthlete(athlete);
        setAnalysisResults(null);
    };

    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        searchAthleteByName(term);
    };

    const searchAthleteByName = (term) => {
        if (!term) {
            setFilteredAthletes(athletes);
            return;
        }
        const searchResults = athletes.filter(athlete => 
            athlete.name.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredAthletes(searchResults);
    };

    const handleLogout = async () => {
        try {
            const auth = getAuth();
            await auth.signOut();
            toast.success("You have been logged out.");
            navigate('/');
        } catch (error) {
            toast.error("Error logging out.");
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteAthlete = async (athleteId) => {
        try {
            const athleteDocRef = doc(db, 'fpnUsers', athleteId);
            await deleteDoc(athleteDocRef);
            toast.success("Athlete deleted successfully.");
            fetchAthletes();
            setSelectedAthlete(null);
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting athlete:", error);
            toast.error("Error deleting athlete.");
        }
    };

    const confirmDeleteTest = async () => {
        try {
            if (selectedAthleteTest.length > 0) {
                const testToDelete = selectedAthleteTest[0];
                const testRef = ref(storage, `fpnResults/${selectedAthlete.id}/${testToDelete.name}`);
                await deleteObject(testRef);
                toast.success("Blood test deleted successfully.");
    
                setAthleteTests((tests) => tests.filter(test => test.name !== testToDelete.name));
    
                setShowDeleteTestModal(false);
                setSelectedAthleteTest([]);
            }
        } catch (error) {
            console.error("Error deleting blood test:", error);
            toast.error("Failed to delete blood test.");
        }
    };

    const handleAnalyzeClick = async (testFile) => {
        if (!selectedAthlete || !testFile) {
            toast.error("Please select an athlete and a blood test");
            return;
        }

        setAnalysisLoading(true);
        setAnalysisResults(null);

        try {
            const testFileRef = ref(storage, `fpnResults/${selectedAthlete.id}/${testFile.name}`);
            const fileURL = await getDownloadURL(testFileRef);
            
            const fileMetadata = await getMetadata(testFileRef);
            
            if (fileMetadata.customMetadata?.analysis) {
                setAnalysisResults(fileMetadata.customMetadata.analysis);
                
                // Retrieve training context used at the time of analysis
                const analysisContext = {
                    trainingPhase: fileMetadata.customMetadata.trainingPhase || trainingPhase,
                    trainingIntensity: fileMetadata.customMetadata.trainingIntensity || trainingIntensity,
                    recoveryState: fileMetadata.customMetadata.recoveryState || recoveryState,
                    sleepQuality: fileMetadata.customMetadata.sleepQuality || sleepQuality,
                    mentalState: fileMetadata.customMetadata.mentalState || mentalState,
                    daysAfterCompetition: fileMetadata.customMetadata.daysAfterCompetition || daysAfterCompetition,
                    recentInjuries: fileMetadata.customMetadata.recentInjuries || recentInjuries,
                    nutritionInfo: fileMetadata.customMetadata.nutritionInfo || nutritionInfo
                };
                
                // Set the context values from metadata
                setDisplayedTrainingContext(analysisContext);
                setAnalysisLoading(false);
                
                if (analysisContainerRef.current) {
                    setTimeout(() => {
                        analysisContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                    }, 500);
                }
                return;
            }
            
            const extractTextResponse = await fetch(`/api/extract-text`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pdfURL: fileURL }),
            });
            
            if (!extractTextResponse.ok) {
                throw new Error('Failed to extract text from PDF');
            }
            
            const extractedData = await extractTextResponse.json();
            const text = extractedData.text;
            
            const athleteProfile = {
                name: selectedAthlete.name,
                age: selectedAthlete.age,
                sex: selectedAthlete.sex,
                height: selectedAthlete.height,
                weight: selectedAthlete.weight,
                sport: selectedAthlete.sport || 'Not specified',
                specialty: selectedAthlete.specialty || 'Not specified',
                trainingPhase: trainingPhase,
                trainingNotes: trainingNotes,
                daysAfterCompetition: parseInt(daysAfterCompetition) || 0,
                trainingIntensity: trainingIntensity,
                recentInjuries: recentInjuries,
                nutritionInfo: nutritionInfo,
                sleepQuality: sleepQuality,
                mentalState: mentalState,
                recoveryState: recoveryState
            };
            
            const analysisResponse = await fetch(`/api/analyze-athlete-result`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text,
                    athlete: athleteProfile
                }),
            });
            
            if (!analysisResponse.ok) {
                throw new Error('Failed to analyze blood test');
            }
            
            const analysisData = await analysisResponse.json();
            setAnalysisResults(analysisData.analysis);
            
            const prognosisMatch = analysisData.analysis.match(/PROGNOSIS:\s*(.*?)(?:\n|$)/);
            const prognosis = prognosisMatch ? prognosisMatch[1].trim() : "Analysis complete";
            
            // Create a training context object to store with this analysis
            const currentTrainingContext = {
                trainingPhase,
                trainingIntensity,
                recoveryState,
                sleepQuality,
                mentalState,
                daysAfterCompetition: daysAfterCompetition || '0',
                recentInjuries,
                nutritionInfo,
                trainingNotes
            };
            
            // Store this in state for display purposes
            setDisplayedTrainingContext(currentTrainingContext);
            
            // Save the analysis and related context to metadata
            await updateMetadata(testFileRef, {
                customMetadata: {
                    ...fileMetadata.customMetadata,
                    analysis: analysisData.analysis,
                    prognosis: prognosis,
                    analysisDate: new Date().toISOString(),
                    // Save training context used for this analysis
                    trainingPhase,
                    trainingIntensity,
                    recoveryState,
                    sleepQuality,
                    mentalState,
                    daysAfterCompetition: daysAfterCompetition || '0',
                    recentInjuries,
                    nutritionInfo
                }
            });
            
            await setDoc(doc(db, 'fpnUsers', selectedAthlete.id), {
                ...selectedAthlete,
                lastAnalysis: {
                    date: new Date(),
                    testName: testFile.name,
                    trainingPhase,
                    trainingNotes,
                    trainingIntensity,
                    recentInjuries,
                    nutritionInfo,
                    sleepQuality,
                    mentalState,
                    recoveryState,
                    daysAfterCompetition
                }
            }, { merge: true });
            
            if (analysisContainerRef.current) {
                setTimeout(() => {
                    analysisContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                }, 500);
            }
            
        } catch (error) {
            console.error("Error analyzing blood test:", error);
            toast.error("Failed to analyze blood test");
        } finally {
            setAnalysisLoading(false);
        }
    };

    const handleViewPdf = (fileUrl) => {
        setSelectedPdfUrl(fileUrl);
        setShowPdfViewer(true);
    };

    const handleClosePdfViewer = () => {
        setShowPdfViewer(false);
        setSelectedPdfUrl('');
    };

    const handleSideBar = () => {
        setNav(!nav);
    };

    const handleProfileImageChange = async (event) => {
        event.stopPropagation();
        
        if (!selectedAthlete) {
            toast.error("Please select an athlete first");
            return;
        }

        const file = event.target.files[0];
        if (!file) return;

        try {
            const imageRef = ref(storage, `fpnProfiles/${selectedAthlete.id}`);
            await uploadBytes(imageRef, file);
            const url = await getDownloadURL(imageRef);
            setProfileImage(url);
            toast.success("Profile image updated");
        } catch (error) {
            console.error("Error uploading profile image:", error);
            toast.error("Failed to upload profile image");
        }
    };

    const handleSettingsClick = () => {
        if (selectedAthlete) {
            setShowEditAthleteModal(true);
        } else {
            toast.error("Please select an athlete first");
        }
    };

    return (
        loadingWindow ? (
            <Spinner />
        ) : (
            <div className="flex h-screen overflow-hidden">
                <aside className={`
                    fixed inset-y-0 left-0 z-50 
                    w-[280px] bg-white border-r border-gray-100
                    transform transition-transform duration-300 ease-in-out
                    ${nav ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                `}>
                    <div className="flex flex-col h-full">
                        <div className="p-4 -mb-4">
                            <button 
                                onClick={handleSideBar}
                                className="md:hidden px-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
    
                        <div className="p-2 border-b border-gray-100">
                            <div className="bg-gradient-to-br from-white via-red-50 to-white rounded-xl p-4 relative overflow-hidden">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <img className="w-24 mx-auto relative" src={BloodFlowLogo} alt="BloodFlow Logo" />
                                <h3 className="text-center text-gray-600 text-md relative">CAR Jamor Portal</h3>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="space-y-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                    <input 
                                        type="text" 
                                        value={searchTerm} 
                                        onChange={handleSearchChange} 
                                        placeholder="Search for an athlete..." 
                                        className="pl-10 p-2.5 border rounded-lg w-full text-sm"
                                    />
                                </div>

                                <div className="flex items-center justify-between p-2">
                                    <div className="flex items-center">
                                        <Users className="w-4 h-4 text-gray-400" />
                                        <span className="ml-2 text-sm font-medium text-gray-600">
                                            Athletes
                                        </span>
                                    </div>
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {filteredAthletes.length}
                                    </span>
                                </div>
    
                                <div className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto">
                                    {filteredAthletes.length > 0 ? (
                                        filteredAthletes.map((athlete, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleAthleteSelect(athlete)}
                                                className={`
                                                    w-full text-left px-3 py-2 rounded-lg text-sm
                                                    ${selectedAthlete?.id === athlete.id 
                                                        ? 'bg-red-50 text-red-700 font-medium' 
                                                        : 'text-gray-600 hover:bg-gray-50'
                                                    }
                                                `}
                                            >
                                                {athlete.name}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="text-center py-6">
                                            <div className="inline-flex p-3 rounded-full bg-red-50 mb-3">
                                                <Users className="w-6 h-6 text-red-500" />
                                            </div>
                                            <p className="text-sm text-gray-500">No athletes found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
    
                        <div className="p-4 border-t border-gray-100">
                            <div className="space-y-2">
                                <button
                                    onClick={handleAddAthleteClick}
                                    className="w-full flex items-center justify-center px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    <span className="text-sm font-medium">Add New Athlete</span>
                                </button>
    
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center justify-center px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    <span className="text-sm font-medium">Logout</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </aside>
    
                <main className="flex-1 md:ml-[280px] flex flex-col h-screen w-screen px-0 -ml-3 md:px-4">
                    <header className="stagger-animation stagger-1 sticky top-0 z-40 bg-white border-b border-gray-100">
                        <div className="flex h-16 items-center justify-between px-4">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={handleSideBar}
                                    className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <Menu className="h-6 w-6 text-gray-500" />
                                </button>
                                <h1 className="text-xl font-semibold text-gray-900">
                                    Athletes Dashboard
                                    <h1 className="text-sm text-black font-light">
                                        Track and analyze athletes' performance
                                    </h1>
                                </h1>
                            </div>
    
                            <div className="flex items-center space-x-4">
                                <button 
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                    onClick={handleSettingsClick}
                                >
                                    <Settings className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                        </div>
                    </header>
    
                    <div className="flex-1 overflow-y-auto bg-gray-50">
                        {showAddAthleteForm && (
                            <div className="stagger-animation stagger-2 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden rounded-lg mx-4 my-6">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2.5 bg-red-50 rounded-xl">
                                            <Users className="w-6 h-6 text-red-500" />
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-900">Add New Athlete</h2>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={newAthleteData.name}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Age*</label>
                                            <input
                                                type="number"
                                                name="age"
                                                value={newAthleteData.age}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Sex*</label>
                                            <select
                                                name="sex"
                                                value={newAthleteData.sex}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                required
                                            >
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)*</label>
                                            <input
                                                type="number"
                                                name="height"
                                                value={newAthleteData.height}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)*</label>
                                            <input
                                                type="number"
                                                name="weight"
                                                value={newAthleteData.weight}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Sport</label>
                                            <input
                                                type="text"
                                                name="sport"
                                                value={newAthleteData.sport}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Specialty</label>
                                            <input
                                                type="text"
                                                name="specialty"
                                                value={newAthleteData.specialty}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                            <textarea
                                                name="notes"
                                                value={newAthleteData.notes}
                                                onChange={handleInputChange}
                                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 resize-none"
                                                rows={3}
                                            ></textarea>
                                        </div>
                                    </div>
                                    <div className="flex justify-end mt-8 space-x-3">
                                        <button 
                                            onClick={() => setShowAddAthleteForm(false)}
                                            className="px-6 py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={addAthlete}
                                            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm"
                                        >
                                            Add Athlete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="stagger-animation stagger-2 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden mx-4 my-6">
                            <div className="absolute inset-0">
                                <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                            </div>

                            <div className="relative">
                                {selectedAthlete ? (
                                    <div className="flex flex-col md:flex-row gap-6">
                                        <div className="w-full md:w-2/3">
                                            <div className="h-full bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl border border-gray-100 hover:border-red-100">
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative group">
                                                            <div className="h-16 w-16 rounded-xl bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center ring-2 ring-white shadow-lg overflow-hidden">
                                                                {profileImage ? (
                                                                    <img 
                                                                        src={profileImage} 
                                                                        alt={selectedAthlete.name} 
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <span className="text-xl font-bold text-white">
                                                                        {selectedAthlete.name[0]}
                                                                    </span>
                                                                )}
                                                                
                                                                <label className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 cursor-pointer flex items-center justify-center">
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={handleProfileImageChange}
                                                                        className="hidden"
                                                                    />
                                                                    <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h3 className="text-xl font-bold text-gray-900">{selectedAthlete.name}</h3>
                                                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                                                <span>{selectedAthlete.age} years</span>
                                                                <span className="text-gray-400">•</span>
                                                                <span>{selectedAthlete.sex}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600">
                                                        {selectedAthlete.sport || 'Athlete'}
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                                    <div className="space-y-4">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="p-1.5 bg-red-50 rounded-lg">
                                                                    <Heart className="w-4 h-4 text-red-500" />
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-700">General Information</span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-gray-500">Height:</span>
                                                                        <span className="text-sm font-medium text-gray-700">{selectedAthlete.height} cm</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-gray-500">Weight:</span>
                                                                        <span className="text-sm font-medium text-gray-700">{selectedAthlete.weight} kg</span>
                                                                    </div>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-gray-500">Sport:</span>
                                                                        <span className="text-sm font-medium text-gray-700">{selectedAthlete.sport || 'N/A'}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-gray-500">Specialty:</span>
                                                                        <span className="text-sm font-medium text-gray-700">{selectedAthlete.specialty || 'N/A'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="p-1.5 bg-red-50 rounded-lg">
                                                                    <FileText className="w-4 h-4 text-red-500" />
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-700">Notes</span>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-600">
                                                                    {selectedAthlete.notes}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="w-full md:w-1/3">
                                            <div className="bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl border border-gray-100 hover:border-red-100 h-full flex flex-col">
                                                <div className="flex items-center gap-3 mb-6">
                                                    <div className="p-2.5 bg-red-50 rounded-xl">
                                                        <Settings className="w-5 h-5 text-red-500" />
                                                    </div>
                                                    <span className="text-lg font-bold text-gray-900">Athlete Management</span>
                                                </div>

                                                <div className="flex-1 flex flex-col justify-center space-y-4">
                                                    <button 
                                                        onClick={() => handleNewFileClick()} 
                                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-sm transform hover:-translate-y-0.5"
                                                    >
                                                        <Upload className="w-4 h-4" />
                                                        <span className="text-sm font-medium">Upload Blood Test</span>
                                                    </button>

                                                    <button 
                                                        onClick={handleDeleteClick} 
                                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-800 text-white hover:bg-black transition-all duration-300 shadow-sm transform hover:-translate-y-0.5"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        <span className="text-sm font-medium">Delete Athlete</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-6 p-8 text-center">
                                        <div className="relative">
                                            <div className="absolute inset-0 bg-red-100 rounded-full blur-xl animate-pulse"></div>
                                            <div className="relative bg-white p-4 rounded-xl shadow-sm border border-red-100">
                                                <Users className="w-8 h-8 text-red-500" />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                                No Athlete Selected
                                            </h2>
                                            <p className="text-gray-600">Select an athlete from the sidebar or add a new one</p>
                                        </div>
                                        
                                        <button
                                            onClick={handleAddAthleteClick}
                                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span>Add New Athlete</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedAthlete && athleteTests.length > 0 && (
                            <div className="stagger-animation stagger-3 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden mx-4 my-6">
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2.5 bg-red-50 rounded-xl">
                                            <Activity className="w-5 h-5 text-red-500" />
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-900">Training Context for Analysis</h2>
                                    </div>
                                    
                                    {/* Main Training Context Form */}
                                    <div className="bg-gray-50 p-6 rounded-xl mb-8">
                                        {/* Training Context Cards */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                            {/* Training Phase & Intensity */}
                                            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-red-100 hover:shadow-md transition-all duration-300">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="p-1.5 bg-red-50 rounded-md">
                                                        <Activity className="w-4 h-4 text-red-500" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700">Training Status</span>
                                                </div>
                                                
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Current Training Phase</label>
                                                        <select
                                                            value={trainingPhase}
                                                            onChange={(e) => setTrainingPhase(e.target.value)}
                                                            className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                        >
                                                            <option value="off-season">Off-Season</option>
                                                            <option value="general-preparation">General Preparation</option>
                                                            <option value="specific-preparation">Specific Preparation</option>
                                                            <option value="competition-phase">Competition Phase</option>
                                                            <option value="high-volume">High Volume Training</option>
                                                            <option value="low-volume">Low Volume Training</option>
                                                            <option value="taper">Taper Period</option>
                                                            <option value="recovery">Recovery Phase</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Training Intensity</label>
                                                        <select
                                                            value={trainingIntensity}
                                                            onChange={(e) => setTrainingIntensity(e.target.value)}
                                                            className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                        >
                                                            <option value="light">Light</option>
                                                            <option value="moderate">Moderate</option>
                                                            <option value="intense">Intense</option>
                                                            <option value="very-intense">Very Intense</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Sleep & Mental State */}
                                            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-red-100 hover:shadow-md transition-all duration-300">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="p-1.5 bg-red-50 rounded-md">
                                                        <Brain className="w-4 h-4 text-red-500" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700">Wellness Factors</span>
                                                </div>
                                                
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Sleep Quality</label>
                                                        <select
                                                            value={sleepQuality}
                                                            onChange={(e) => setSleepQuality(e.target.value)}
                                                            className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                        >
                                                            <option value="poor">Poor</option>
                                                            <option value="fair">Fair</option>
                                                            <option value="good">Good</option>
                                                            <option value="excellent">Excellent</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Mental State</label>
                                                        <select
                                                            value={mentalState}
                                                            onChange={(e) => setMentalState(e.target.value)}
                                                            className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                        >
                                                            <option value="stressed">Stressed</option>
                                                            <option value="fatigued">Fatigued</option>
                                                            <option value="stable">Stable</option>
                                                            <option value="motivated">Motivated</option>
                                                            <option value="highly-motivated">Highly Motivated</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Recovery & Competition */}
                                            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-red-100 hover:shadow-md transition-all duration-300">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="p-1.5 bg-red-50 rounded-md">
                                                        <FileText className="w-4 h-4 text-red-500" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-700">Recovery Status</span>
                                                </div>
                                                
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Recovery State</label>
                                                        <select
                                                            value={recoveryState}
                                                            onChange={(e) => setRecoveryState(e.target.value)}
                                                            className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                        >
                                                            <option value="poor">Poor</option>
                                                            <option value="normal">Normal</option>
                                                            <option value="good">Good</option>
                                                            <option value="optimized">Optimized</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1.5">Days After Competition</label>
                                                        <input
                                                            type="number"
                                                            value={daysAfterCompetition}
                                                            onChange={(e) => setDaysAfterCompetition(e.target.value)}
                                                            placeholder="Enter number of days"
                                                            className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status Indicator Badges */}
                                        <div className="flex flex-wrap gap-3 mb-8">
                                            <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                                                trainingIntensity === 'light' ? 'bg-blue-100 text-blue-700' :
                                                trainingIntensity === 'moderate' ? 'bg-green-100 text-green-700' :
                                                trainingIntensity === 'intense' ? 'bg-orange-100 text-orange-700' :
                                                'bg-red-100 text-red-700'
                                            }`}>
                                                <Activity className="w-3 h-3" />
                                                <span>{trainingIntensity} intensity</span>
                                            </div>

                                            <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                                                trainingPhase === 'recovery' ? 'bg-teal-100 text-teal-700' :
                                                trainingPhase === 'competition-phase' ? 'bg-purple-100 text-purple-700' :
                                                'bg-gray-100 text-gray-700'
                                            }`}>
                                                <span>{trainingPhase.replace(/-/g, ' ')}</span>
                                            </div>

                                            <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                                                sleepQuality === 'poor' ? 'bg-red-100 text-red-700' :
                                                sleepQuality === 'good' ? 'bg-green-100 text-green-700' :
                                                sleepQuality === 'excellent' ? 'bg-blue-100 text-blue-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                <span>{sleepQuality} sleep</span>
                                            </div>

                                            <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                                                recoveryState === 'poor' ? 'bg-red-100 text-red-700' :
                                                recoveryState === 'good' ? 'bg-green-100 text-green-700' :
                                                recoveryState === 'optimized' ? 'bg-blue-100 text-blue-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                <span>{recoveryState} recovery</span>
                                            </div>

                                            <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                                                mentalState === 'stressed' ? 'bg-red-100 text-red-700' :
                                                mentalState === 'motivated' || mentalState === 'highly-motivated' ? 'bg-green-100 text-green-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                <span>{mentalState} mental state</span>
                                            </div>

                                            {daysAfterCompetition && (
                                                <div className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium flex items-center gap-1.5">
                                                    <span>{daysAfterCompetition} days post-comp</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Additional Info Cards */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-red-100 hover:shadow-md transition-all duration-300">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="p-1.5 bg-red-50 rounded-md">
                                                        <Activity className="w-4 h-4 text-red-500" />
                                                    </div>
                                                    <label className="text-sm font-medium text-gray-700">Recent Injuries</label>
                                                </div>
                                                <select
                                                    value={recentInjuries ? 'yes' : 'no'}
                                                    onChange={(e) => setRecentInjuries(e.target.value === 'yes' ? 'Yes, has recent injuries' : '')}
                                                    className="w-full p-2.5 border rounded-lg text-sm mb-3 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                >
                                                    <option value="no">No recent injuries</option>
                                                    <option value="yes">Yes, has recent injuries</option>
                                                </select>
                                                {recentInjuries && (
                                                    <textarea
                                                        value={recentInjuries}
                                                        onChange={(e) => setRecentInjuries(e.target.value)}
                                                        placeholder="Describe any recent injuries, when they occurred, and current status"
                                                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 resize-none"
                                                        rows={2}
                                                    ></textarea>
                                                )}
                                            </div>
                                            
                                            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-red-100 hover:shadow-md transition-all duration-300">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="p-1.5 bg-red-50 rounded-md">
                                                        <FileText className="w-4 h-4 text-red-500" />
                                                    </div>
                                                    <label className="text-sm font-medium text-gray-700">Nutrition Information</label>
                                                </div>
                                                <select
                                                    value={nutritionInfo ? 'custom' : 'standard'}
                                                    onChange={(e) => setNutritionInfo(e.target.value === 'custom' ? 'Custom nutrition plan' : '')}
                                                    className="w-full p-2.5 border rounded-lg text-sm mb-3 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200"
                                                >
                                                    <option value="standard">Standard nutrition plan</option>
                                                    <option value="custom">Custom nutrition plan</option>
                                                </select>
                                                {nutritionInfo && (
                                                    <textarea
                                                        value={nutritionInfo}
                                                        onChange={(e) => setNutritionInfo(e.target.value)}
                                                        placeholder="Describe any dietary changes, supplements, or nutrition plans"
                                                        className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 resize-none"
                                                        rows={2}
                                                    ></textarea>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Training Notes */}
                                        <div className="mt-4 bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-red-100 hover:shadow-md transition-all duration-300">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="p-1.5 bg-red-50 rounded-md">
                                                    <FileText className="w-4 h-4 text-red-500" />
                                                </div>
                                                <label className="text-sm font-medium text-gray-700">Additional Training Notes</label>
                                            </div>
                                            <textarea
                                                value={trainingNotes}
                                                onChange={(e) => setTrainingNotes(e.target.value)}
                                                placeholder="Include any other relevant information about the athlete's current training regimen"
                                                className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 resize-none"
                                                rows={3}
                                            ></textarea>
                                        </div>
                                    </div>

                                    {selectedAthlete && athleteTests.length > 0 && (
                                        <div className="relative mt-6">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="p-2.5 bg-red-50 rounded-xl">
                                                    <Brain className="w-5 h-5 text-red-500" />
                                                </div>
                                                <h2 className="text-xl font-bold text-gray-900">Blood Test Results</h2>
                                            </div>
                                            
                                            <TestResultsViewer 
                                                tests={athleteTests}
                                                onSelectTest={(test) => handleAnalyzeClick(test)}
                                                onViewPdf={handleViewPdf}
                                                onDeleteTest={(test) => {
                                                    setSelectedAthleteTest([test]);
                                                    setShowDeleteTestModal(true);
                                                }}
                                            />
                                        </div>
                                    )} 
                                </div>
                            </div>
                        )}

                        {(analysisResults || analysisLoading) && (
                            <div 
                                ref={analysisContainerRef}
                                className="stagger-animation stagger-4 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden mx-4 my-6"
                            >
                                <div className="absolute inset-0">
                                    <div className="absolute -right-4 -top-4 w-32 h-32 bg-red-100/50 rounded-full blur-xl"></div>
                                    <div className="absolute -left-4 -bottom-4 w-32 h-32 bg-red-50/50 rounded-full blur-xl"></div>
                                    <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.3),rgba(255,255,255,0.2))] backdrop-blur-sm"></div>
                                </div>

                                <div className="relative">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 bg-red-50 rounded-xl">
                                                <ChartBar className="w-5 h-5 text-red-500" />
                                            </div>
                                            <h2 className="text-xl font-bold text-gray-900">Blood Test Analysis Results</h2>
                                        </div>
                                        
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="px-3 py-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs rounded-full">
                                                {displayedTrainingContext.trainingPhase.replace("-", " ")}
                                            </span>
                                            
                                            <span className={`px-3 py-1 text-xs rounded-full ${
                                                displayedTrainingContext.trainingIntensity === 'light' ? 'bg-blue-100 text-blue-600' :
                                                displayedTrainingContext.trainingIntensity === 'moderate' ? 'bg-green-100 text-green-600' :
                                                displayedTrainingContext.trainingIntensity === 'intense' ? 'bg-orange-100 text-orange-600' :
                                                'bg-purple-100 text-purple-600'
                                            }`}>
                                                {displayedTrainingContext.trainingIntensity} training
                                            </span>
                                            
                                            <span className={`px-3 py-1 text-xs rounded-full ${
                                                displayedTrainingContext.recoveryState === 'poor' ? 'bg-red-100 text-red-600' :
                                                displayedTrainingContext.recoveryState === 'normal' ? 'bg-yellow-100 text-yellow-600' :
                                                displayedTrainingContext.recoveryState === 'good' ? 'bg-teal-100 text-teal-600' :
                                                'bg-indigo-100 text-indigo-600'
                                            }`}>
                                                {displayedTrainingContext.recoveryState} recovery
                                            </span>
                                        </div>
                                    </div>

                                    {analysisLoading ? (
                                        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                                            <p className="text-lg font-medium text-gray-600">Analyzing blood test results...</p>
                                            <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
                                        </div>
                                    ) : (
                                        <AthleteAnalysisDisplay analysisText={analysisResults} />
                                    )}
                                </div>
                            </div>
                        )}

                        <footer className="relative stagger-animation stagger-5 bg-white p-8 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden mx-4 my-6">
                            <div className="absolute inset-0 overflow-hidden">
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-[500px] h-[200px]"></div>
                                <div className="absolute inset-0 bg-gradient-to-b from-red-50/50 to-transparent blur-3xl"></div>
                            </div>

                            <div className="relative">
                                <div className="flex justify-center">
                                    <div className="relative group">
                                        <div className="absolute inset-0 bg-red-100 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                                        <img 
                                            className="w-32 md:w-40 relative transform hover:scale-105 transition-transform duration-300" 
                                            src={BloodFlowLogoNL} 
                                            alt="BloodFlow Logo" 
                                        />
                                    </div>
                                </div>

                                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                                    <div className="flex flex-col items-center gap-4">
                                        <p className="text-center text-sm text-gray-600 font-medium">
                                            © {new Date().getFullYear()} BloodFlow. All Rights Reserved
                                        </p>

                                        <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-red-200 to-transparent"></div>

                                        <p className="text-center text-xs text-gray-500">
                                            Powering healthcare with advanced AI analytics
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </footer>
                    </div>
                </main>

                {nav && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                        onClick={handleSideBar}
                    />
                )}
    
                {showNewFileModal && (
                    <NewFilePopup
                        show={showNewFileModal}
                        onClose={() => {
                            setShowNewFileModal(false);
                            setSelectedAthleteTest([]);
                        }}
                        onConfirm={() => uploadBloodTest(selectedAthlete.id)}
                        fileName={selectedAthleteTest.map(file => file.name || file)}
                    />
                )}
                
                {showDeleteModal && selectedAthlete && (
                    <DeleteAthletePopup
                        show={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={() => confirmDeleteAthlete(selectedAthlete.id)}
                        athleteName={selectedAthlete.name}
                    />
                )}
                
                <DeleteTestPopup 
                    show={showDeleteTestModal}
                    onClose={() => {
                        setShowDeleteTestModal(false);
                        setSelectedAthleteTest([]);
                    }}
                    onConfirm={confirmDeleteTest}
                    testName={selectedAthleteTest.length > 0 ? selectedAthleteTest[0].name : ''}
                />

                {showPdfViewer && selectedPdfUrl && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                        <div className="relative w-full max-w-6xl h-[90vh] bg-white rounded-lg p-2">
                            <button 
                                onClick={handleClosePdfViewer}
                                className="absolute top-4 right-4 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 transition-colors duration-300 z-10"
                            >
                                <X className="h-6 w-6" />
                            </button>
                            <iframe 
                                src={selectedPdfUrl} 
                                className="w-full h-full rounded" 
                                title="PDF Viewer" 
                            />
                        </div>
                    </div>
                )}

                <EditAthletePopup 
                    show={showEditAthleteModal} 
                    onClose={() => setShowEditAthleteModal(false)} 
                    athlete={selectedAthlete}
                    onSave={(updatedData) => {
                        setShowEditAthleteModal(false);
                        fetchAthletes();
                        if (selectedAthlete) {
                            setSelectedAthlete({...selectedAthlete, ...updatedData});
                        }
                    }}
                />
            </div>)
    );
};

export default AthletesDashboard;
